<template>
  <div class="relative">
    <!-- 🔵 Loader -->
    <div
      v-if="isLoading"
      class="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-50 rounded-xl"
    >
      <div class="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-600"></div>
    </div>

    <!-- 🔹 Header -->
    <div class="flex justify-between items-center flex-wrap gap-3 w-full">
      <div class="flex items-center gap-3">
        <h1 class="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <i class="ri-global-line text-blue-600 text-2xl"></i> My Websites
        </h1>
        <span class="ml-2 px-3 py-1 text-xs bg-blue-100 text-blue-700 font-medium rounded-full">
          {{ totalRecords }} Connected
        </span>
      </div>

      <button
        @click="gotoweb"
        class="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 hover:shadow-md transition-all flex items-center gap-2"
      >
        <i class="ri-add-line text-lg"></i> Add Website
      </button>
    </div>

    <p class="text-sm text-gray-500 mt-2">
      Manage and monitor your connected websites with real-time analytics.
    </p>

    <!-- 🔹 Status Message -->
    <transition name="fade">
      <div
        v-if="msgTxt"
        :class="`p-3 mt-4 rounded-lg text-sm ${
          msgType === 'success'
            ? 'bg-green-100 text-green-700'
            : 'bg-red-100 text-red-700'
        }`"
      >
        {{ msgTxt }}
      </div>
    </transition>
    <!-- 🔹 Tabs Section (Restored) -->
    <div class="flex gap-3 border-b border-gray-200 mb-4 overflow-x-auto">
      <button
        v-for="tab in tabs"
        :key="tab.value"
        @click="setActiveTab(tab.value)"
        :class="[
          'px-4 py-2 font-medium whitespace-nowrap transition-all duration-200',
          activeTab === tab.value
            ? 'border-b-2 border-blue-600 text-blue-600'
            : 'text-gray-500 hover:text-blue-600'
        ]"
      >
        {{ tab.label }}
        <span
          v-if="tab.count !== undefined"
          class="ml-1 text-xs bg-gray-100 px-2 py-0.5 rounded-full"
        >
          {{ tab.count }}
        </span>
      </button>
    </div>


    <!-- 🔹 Website Cards -->
    <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-10">
      <div
        v-for="(site, index) in websites"
        :key="index"
        class="card border border-gray-100 hover:shadow-lg transition-all duration-300 relative overflow-hidden flex flex-col justify-between"
      >
        <div class="absolute top-0 left-0 w-full h-1 bg-blue-500"></div>

        <div class="flex-1 flex flex-col justify-center px-4">
          <div class="flex items-center justify-between mb-5">
            <div class="flex items-center gap-3">
              <img
                :src="`https://www.google.com/s2/favicons?sz=64&domain=${site.web_link}`"
                alt="favicon"
                class="w-8 h-8 rounded-sm"
              />
              <div>
                <h3 class="text-lg font-semibold text-gray-800 truncate">
                  {{ site.web_link }}
                </h3>
                <p
                  :class="checkAct1(site.visitors)"
                >
                  {{ checkAct(site.visitors) }}
                </p>
              </div>
            </div>
            <!-- <button
              class="text-gray-400 hover:text-blue-600 transition"
              title="Options"
            >
              <i class="ri-more-2-line text-lg"></i>
            </button> -->
          </div>
          <hr />
          <br />

          <div
            class="grid grid-cols-2 gap-x-8 gap-y-4 text-base text-gray-700 justify-center text-center"
          >
            <div>
              <p class="text-xs text-gray-500 mb-1">Visitors</p>
              <p class="font-semibold text-lg text-gray-900">{{ site.visitors || 0 }}</p>
            </div>
            <div>
              <p class="text-xs text-gray-500 mb-1">Pageviews</p>
              <p class="font-semibold text-lg text-gray-900">{{ site.pageviews || 0 }}</p>
            </div>
            <div>
              <p class="text-xs text-gray-500 mb-1">Bounce Rate</p>
              <p class="font-semibold text-lg text-gray-900">{{ site.bounce || 0 }}</p>
            </div>
            <div>
              <p class="text-xs text-gray-500 mb-1">Timezone</p>
              <p class="font-semibold text-lg text-gray-900">{{ site.web_timezone || "N/A" }}</p>
            </div>
          </div>
        </div>

        <div
          class="flex justify-between items-center gap-2 mt-5 pt-4 border-t border-gray-100"
        >
          <button
            class="btn btn-blue flex-1 border border-gray-200 rounded-full"
            @click="viewAnalytics(site)"
          >
            <i class="ri-bar-chart-line text-base"></i> Analytics
          </button>
          <button
            class="btn btn-green flex-1 border border-gray-200 rounded-full"
            @click="copyScript(site)"
          >
            <i class="ri-file-copy-line text-base"></i> Script
          </button>
          <button
            class="btn btn-red flex-1 border border-gray-200 rounded-full"
            @click="confirmDelete(site)"
          >
            <i class="ri-delete-bin-line text-base"></i> Delete
          </button>
        </div>
      </div>
    </div>

    <!-- 🔹 Pagination -->
    <div
      class="flex justify-between items-center mt-10 border-t border-gray-100 pt-5"
    >
      <p class="text-sm text-gray-500">
        Showing Page {{ page_no + 1 }} of {{ totalPages }}
      </p>
      <div class="flex items-center gap-2">
        <button
          @click="prevPage"
          :disabled="page_no === 0"
          class="px-3 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          <i class="ri-arrow-left-s-line"></i> Prev
        </button>
        <span class="text-sm font-medium text-gray-700"
          >Page {{ page_no + 1 }}</span
        >
        <button
          @click="nextPage"
          :disabled="page_no + 1 >= totalPages"
          class="px-3 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          Next <i class="ri-arrow-right-s-line"></i>
        </button>
      </div>
    </div>

    <!-- 🗑️ Delete Confirmation Modal -->
   <!-- 🗑️ Delete Confirmation Modal (Centered Top) -->
<transition name="fade">
  <div
    v-if="showDeleteModal"
    class="fixed inset-0 z-[9999] flex justify-center items-start bg-black/50 backdrop-blur-sm"
  >
    <!-- Modal Box -->
    <div
      class="bg-white rounded-2xl shadow-2xl w-[90%] max-w-md p-6 text-center mt-[10vh] animate-popUp relative"
    >
      <!-- Close -->
      <button
        @click="showDeleteModal = false"
        class="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
      >
        <i class="ri-close-line text-2xl"></i>
      </button>

      <!-- Icon & Title -->
      <div class="flex flex-col items-center mb-4">
        <div class="w-16 h-16 flex items-center justify-center bg-red-100 text-red-600 rounded-full mb-3">
          <i class="ri-delete-bin-5-line text-3xl"></i>
        </div>
        <h3 class="text-lg font-semibold text-gray-800 mb-1">Confirm Deletion</h3>
        <p class="text-gray-600 text-sm">
          Are you sure you want to delete
          <strong>{{ selectedSite?.web_link }}</strong>?
        </p>
      </div>

      <!-- Buttons -->
      <div class="flex justify-center gap-3 mt-5">
        <button
          @click="performDelete"
          class="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 shadow-sm transition"
        >
          Yes, Delete
        </button>
        <button
          @click="showDeleteModal = false"
          class="px-5 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
</transition>

  </div>
</template>


<script>
import { getBookingList, deleteBookingById } from "@/apis/bookingpageapis";
import { getMyDt } from "@/apis/webapis";

export default {
  name: "MyWebsites",
  data() {
    return {
    tabs: [
      { label: "All", value: "all", count: 0 },
      { label: "Active", value: "active", count: 0 },
      { label: "Inactive", value: "inactive", count: 0 }
    ],
    activeTab: "all",
      websites: [],
      ldata: null,
      isLoading: false,
      msgType: "",
      msgTxt: "",
      page_no: 0,
      limit: 10,
      totalRecords: 0,
      totalPages: 1,
      showDeleteModal: false,
      selectedSite: null,
    };
  },
  methods: {
     setActiveTab(tabValue) {
      this.activeTab = tabValue;
      this.getWebsites(0);
    },
    async getMyDt1(){
       try {
        const res = await getMyDt(
          this.ldata.user_id
        );
        if (res.status && res.user_dt) {
          this.ldata = res.user_dt;
          sessionStorage.setItem("ldata",JSON.stringify(this.ldata));
         
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    checkAct($visits){
      if($visits>0){
        return "Active";
      }
      return "Inactive";
    },
    checkAct1($visits){
      if($visits>0){
        return "text-xs px-2 py-0.5 inline-block rounded-full font-medium mt-1 bg-green-100 text-green-700";
      }
      return "text-xs px-2 py-0.5 inline-block rounded-full font-medium mt-1 bg-red-100 text-red-700";
    },
    gotoweb() {
      window.location.href = "/add_website";
    },
    async getWebsites(page = 0) {
      try {
        this.isLoading = true;
        const res = await getBookingList(page, this.limit, this.ldata.user_id,this.activeTab);
        if (res.status) {
          this.websites = res.records;
          this.totalRecords = parseInt(res.total1);
          this.totalPages = Math.ceil(this.totalRecords / this.limit);
          this.page_no = page;

          this.tabs.find((t) => t.value === "all").count = parseInt(res.activeCount)+parseInt(res.inactiveCount);
          this.tabs.find((t) => t.value === "active").count = parseInt(res.activeCount);
          this.tabs.find((t) => t.value === "inactive").count = parseInt(res.inactiveCount);
        } else {
          this.msgType = "danger";
          this.msgTxt = res.message || "Failed to fetch data.";
        }
      } catch {
        this.msgType = "danger";
        this.msgTxt = "Error loading data.";
      } finally {
        this.isLoading = false;
      }
    },
    confirmDelete(site) {
      this.selectedSite = site;
      this.showDeleteModal = true;
    },
    async performDelete() {
      this.showDeleteModal = false;
      this.isLoading = true;
      try {
        const res = await deleteBookingById(this.selectedSite.web_id);
        if (res.status) {
          this.msgType = "success";
          this.msgTxt = "Website deleted successfully.";
          this.getWebsites(this.page_no);
        } else {
          this.msgType = "danger";
          this.msgTxt = res.message || "Failed to delete website.";
        }
      } catch {
        this.msgType = "danger";
        this.msgTxt = "Network error while deleting.";
      } finally {
        this.isLoading = false;
      }
    },
    viewAnalytics(site) {
      window.location.href = `/?web_id=${site.web_id}&web_name=${site.web_link}`;
    },
    async copyScript(site) {
      const script =
        "<scr" +
        'ipt async defer data-domain="' +
        site.web_link +
        '" data-api="https://api.analytcly.ai/pls/event" ' +
        'src="https://api.analytcly.ai/pls/script"></scr' +
        "ipt>";
      try {
        await navigator.clipboard.writeText(script);
        this.msgType = "success";
        this.msgTxt = "Script copied to clipboard!";
      } catch {
        this.msgType = "danger";
        this.msgTxt = "Failed to copy script.";
      }
    },
    nextPage() {
      if (this.page_no + 1 < this.totalPages) this.getWebsites(this.page_no + 1);
    },
    prevPage() {
      if (this.page_no > 0) this.getWebsites(this.page_no - 1);
    },
  },
  created() {
    if (sessionStorage.getItem("ldata")) {
      this.ldata = JSON.parse(sessionStorage.getItem("ldata"));
      this.getMyDt1();
      this.getWebsites(0);
    }
  },
};
</script>

<style scoped>
.card {
  @apply bg-white border border-gray-100 rounded-xl p-7 shadow-md transition-all duration-300 flex flex-col;
  min-height: 280px;
}
.card:hover {
  @apply shadow-xl -translate-y-1;
}
.btn {
  @apply flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-full font-medium transition-all duration-200 border border-gray-200;
}
.btn-blue {
  @apply bg-blue-50 text-blue-600 hover:bg-blue-100;
}
.btn-green {
  @apply bg-green-50 text-green-600 hover:bg-green-100;
}
.btn-red {
  @apply bg-red-50 text-red-600 hover:bg-red-100;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes popUp {
  from {
    transform: translateY(-10px);
    opacity: 0.6;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
.animate-popUp {
  animation: popUp 0.3s ease-out;
}

</style>